/*
 * � 2025, NTT Data Group. All rights reserved. See LICENSE.txt file in the project root for third party licenses info.
 */

import { AsyncPipe, DOCUMENT } from "@angular/common";
import { Component, inject, OnInit } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { EChartsOption, EChartsType } from "echarts";
import { combineLatest, defer, map, startWith, Subject } from "rxjs";
import { ButtonComponent } from "../../../../shared/components/button/button.component";
import { ChartComponent } from "../../../../shared/components/chart/chart.component";
import { DropdownComponent } from "../../../../shared/components/dropdown/dropdown.component";
import { ScatterChart } from "../../../../shared/models/http/charts/scatter-chart/scatter-chart.model";
import { AdvancedFilters } from "../../../../shared/models/http/dashboard/advanced-filters.model";
import { ChartResponse } from "../../../../shared/models/ui/charts.model";
import { SelectOption } from "../../../../shared/models/ui/select-option.model";
import { SoftwareAssetsGlobalForm } from "../../../../shared/models/ui/software-assets-global-form.model";
import { AuthorizationService } from "../../../../shared/services/authorization.service";
import { DashboardSciService } from "../../../../shared/services/dashboard-sci.service";
import { UsersHttpService } from "../../../../shared/services/http/users-http.service";
import { DashboardStateService } from "../../../../shared/services/state/dashboard-state.service";
import { GRANTS } from "../../../../shared/utils/grants.constants";
import { lazyLoad } from "../../../../shared/utils/lazy-load";
import { formatDateForBE, saveAs } from "../../../../shared/utils/utils";
@Component({
	selector: "globalictcc-software-carbon-intensity",
	standalone: true,
	imports: [AsyncPipe, ChartComponent, DropdownComponent, ButtonComponent, FormsModule],
	templateUrl: "./software-carbon-intensity.component.html",
	styleUrl: "./software-carbon-intensity.component.scss"
})
export class SoftwareCarbonIntensityComponent implements OnInit {
	private readonly _document: Document = inject(DOCUMENT);
	public readonly grants = GRANTS.swart;
	public scatterOverrides: EChartsOption = {
		xAxis: {
			name: "KgCO2e"
		},
		yAxis: {
			name: "SCI"
		}
	};
	public functionalUnitsOptions: SelectOption[];
	public selectedFunctionalUnit: string;

	private _nextCursor: string | null = null;
	public hasMoreFunctionalUnits: boolean = false;

	private static readonly _cursorsSessionKey = "sci_functional_units_cursor";

	private readonly _apply$ = new Subject<void>();
	private readonly _worst10EmissionsRefresh$ = new Subject<void>();
	private readonly _greenScoreRefresh$ = new Subject<void>();
	// Charts reload on global form changes and on Apply, with the functional unit selected at that time.
	private readonly _filters$ = combineLatest([
		defer(() => this._dashboardStateService.softwareAssetsGlobalForm$),
		this._apply$.pipe(startWith(undefined))
	]).pipe(map(([form]) => this._buildAdvancedFilters(form)));

	// Streams are cold: an API is only called while a rendered widget subscribes to it.
	public readonly worst10Emissions$ = lazyLoad(
		this._filters$,
		filters => this._dashboardSciService.getWorst10Emissions(filters),
		this._worst10EmissionsRefresh$
	);

	public readonly greenScore$ = lazyLoad(
		this._filters$,
		filters =>
			this._dashboardSciService
				.getGreenScore(filters)
				.pipe(map(greenScore => this._swapGreenScoreAxes(greenScore))),
		this._greenScoreRefresh$
	);

	constructor(
		private _dashboardSciService: DashboardSciService,
		private _dashboardStateService: DashboardStateService,
		private _usersHttpService: UsersHttpService,
		public authorizationService: AuthorizationService
	) {}

	ngOnInit() {
		this._loadFunctionalUnitsPage();
	}

	private _loadFunctionalUnitsPage() {
		this._usersHttpService.getFunctionalUnits(this._nextCursor).subscribe(response => {
			this._nextCursor = response.nextCursor;
			this.hasMoreFunctionalUnits = response.hasNext;

			if (response.nextCursor) {
				sessionStorage.setItem(
					SoftwareCarbonIntensityComponent._cursorsSessionKey,
					response.nextCursor
				);
			} else {
				sessionStorage.removeItem(SoftwareCarbonIntensityComponent._cursorsSessionKey);
			}

			const newOptions = response.content.map(item => ({
				label: item.name,
				value: item.name
			}));
			this.functionalUnitsOptions = [...(this.functionalUnitsOptions ?? []), ...newOptions];

			if (!this.selectedFunctionalUnit) {
				this.selectedFunctionalUnit = this.functionalUnitsOptions[0]?.value || "";
			}
		});
	}

	public onFunctionalUnitsScrolledToBottom() {
		if (this.hasMoreFunctionalUnits) this._loadFunctionalUnitsPage();
	}

	/**
	 * Handles the selection of a functional unit.
	 *
	 * @param functionalUnit - The selected functional unit, which can be a string or an array of strings.
	 */
	public onFunctionalUnitSelect(functionalUnit: string | string[]) {
		this.selectedFunctionalUnit = functionalUnit as string;
	}

	/**
	 * Reloads the charts with the currently selected functional unit.
	 */
	public onApply() {
		this._apply$.next();
	}

	private _swapGreenScoreAxes(
		response: ChartResponse<ScatterChart>
	): ChartResponse<ScatterChart> {
		if (!response.chart.scatterPoints) return response;
		return {
			...response,
			chart: {
				...response.chart,
				scatterPoints: response.chart.scatterPoints.map(point => ({
					...point,
					value: [point.value[1], point.value[0]]
				}))
			}
		};
	}

	public onExportChart(chartInstance?: EChartsType, imageName?: string) {
		if (!chartInstance) return;

		const originalOption = chartInstance.getOption();
		const optionWithoutDataZoom = {
			...originalOption,
			dataZoom: []
		};

		chartInstance.setOption(optionWithoutDataZoom, { notMerge: true });

		setTimeout(() => {
			const chartAsPngUrl = chartInstance.getDataURL({ type: "png" });
			saveAs(this._document, chartAsPngUrl, imageName || "chart");
			chartInstance.setOption(originalOption, { notMerge: true });
		}, 300);
	}

	public onWorst10EmissionsChartRefresh() {
		this._worst10EmissionsRefresh$.next();
	}

	public onGreenScoreChartRefresh() {
		this._greenScoreRefresh$.next();
	}

	private _buildAdvancedFilters(form: SoftwareAssetsGlobalForm): AdvancedFilters | undefined {
		const startDate = formatDateForBE(form.timePeriod?.start);
		const endDate = formatDateForBE(form.timePeriod?.end);
		if (!this.selectedFunctionalUnit || !startDate || !endDate) return undefined;

		const { applicationsSelection, layersSelection } = form;
		return {
			startDate,
			endDate,
			functionalUnit: this.selectedFunctionalUnit,
			allApplicationsSelected: applicationsSelection.allSelected,
			allLayersSelected: layersSelection.allSelected,
			applicationsSelection: {
				selectedIds: applicationsSelection.selectedIds,
				excludedIds: applicationsSelection.excludedIds
			},
			layersSelection: {
				selectedIds: layersSelection.selectedIds,
				excludedIds: layersSelection.excludedIds
			}
		};
	}
}
